import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseResultModel, LanguageService, PaymentType, StepModel, TransportMode } from '@hazmatsuite-next/ui';
import { BehaviorSubject, map, Observable, of, take, tap } from 'rxjs';
import { EnumPackageType } from '../models/package-type.enum';
import { PackagingType } from '../models/packaging-type.model';
import { SinglePackagingModel } from '../models/single-packaging.model';
import { PackagingModel } from '../models/packaging.model';
import { Store } from '@ngrx/store';
import { CompositePackagingModel } from '../models/composite-packaging.model';
import { OverPackagingModel } from '../models/over-packaging.model';
import { ShippingPaper } from '../models/shipping-paper.model';
import { EasyDgdDataModel } from '../models/easydgd-data.model';
import { IDocument } from '../models/document.interface';
import { Packages } from '../models/packages.interface';
import { EasyDgdState } from '../models/easydgd-state.model';
import { PreviewImageViewModel } from '../models/preview-image.interface';
import { DownloadModel } from '../models/download.interface';
import { ShareDocumentModel } from '../models/share-document.interface';
import { InnerPackagingModel } from '../models/inner-packaging.model';
import * as uuid from 'uuid';
import { CanLoadTogetherResult } from '../models/can-load-together-result.interface';
import { GefahrgutApiDangerousGoodItem } from '../models/gefahrgut-api-dangerous-good-item.interface';

@Injectable({
  providedIn: 'root'
})
export class EasyDgdService {


  private state = new EasyDgdState();

  public downloadModel?: DownloadModel;

  public get initialized(): boolean {
    return this.state.sessionLoaded;
  }
  public get data(): EasyDgdDataModel {
    return this.state.data;
  }

  constructor(private http: HttpClient, private languageService: LanguageService, private router: Router, private store: Store<{ packageList: { packages: Packages } }>) {

  }


  public reset() {
    return this.resetModel().pipe(tap(() => {
      this.state = new EasyDgdState();
    }));
  }

  public setTransportMode(transportMode: TransportMode): void {
    if (this.state.data.TransportMode !== transportMode) {
      this.state.sessionLoaded = false;
    }

    this.state.data.ShippingPaper = undefined;
    this.state.data.TransportMode = transportMode;
  }

  public getTransportMode(): TransportMode {
    return this.state.data.TransportMode;
  }

  public setGeneralInformationData(shippingPaper: ShippingPaper): void {
    this.state.data.ShippingPaper = shippingPaper;
  }

  public getGeneralInformationData(): ShippingPaper {
    return this.state.data.ShippingPaper || new ShippingPaper();
  }

  setUuidIfNecessary(packagingModel: PackagingModel) {
    if (!packagingModel.Id) {
      packagingModel.Id = uuid.v4();
    }
  }

  public saveData(): Observable<boolean> {
    return this.putData(this.state.data);
  }

  public loadDataFromSession(): Observable<EasyDgdDataModel> {
    return this.http.get<EasyDgdDataModel>(`${this.languageService.getLanguage()}/EasyDgd/Data`).pipe(
      tap(data => {
        this.setSessionData(data || {})
        // this.setActiveStep(data?.Step || EasyDgdStep.TransportMode);
      }),
    )
  }

  public loadDocuments(): Observable<IDocument[]> {
    return this.http.get<IDocument[]>(`${this.languageService.getLanguage()}/EasyDgd/Documents`);
  }

  public setSessionData(data: EasyDgdDataModel): void {
    this.state.sessionLoaded = true;
    this.state.data = data;
    // this.updateSteps();
  }

  editShippingPaper(id: number): Observable<EasyDgdDataModel> {
    return this.http.get<EasyDgdDataModel>(`${this.languageService.getLanguage()}/EasyDgd/Edit/${id}`).pipe(
      take(1),
      tap((data: EasyDgdDataModel) => {
        this.state.data = data;
      })
    );
  }

  cloneShippingPaper(id: number): Observable<EasyDgdDataModel> {
    return this.http.get<EasyDgdDataModel>(`${this.languageService.getLanguage()}/EasyDgd/Clone/${id}`).pipe(
      take(1),
      tap((data: EasyDgdDataModel) => {
        this.state.data = data;
      })
    );
  }

  deleteShippingPaper(id: number): Observable<BaseResultModel> {
    return this.http.delete<BaseResultModel>(`${this.languageService.getLanguage()}/EasyDgd/Delete/${id}`);
  }

  loadPackages(): Observable<Packages> {
    return this.http.get<Packages>(`${this.languageService.getLanguage()}/EasyDgd/Packages`);
  }

  loadPackagingTypes(packageType: EnumPackageType, transportMode: TransportMode): Observable<PackagingType[]> {
    return this.http.get<PackagingType[]>(`${this.languageService.getLanguage()}/EasyDgd/PackagingTypes?packageType=${packageType}&transportMode=${transportMode}`);
  }

  loadUnPackagingTypes(packageTypeId: number, transportMode: TransportMode): Observable<PackagingType[]> {
    return this.http.get<PackagingType[]>(`${this.languageService.getLanguage()}/EasyDgd/UnPackagingTypes?packagingTypeId=${packageTypeId}&transportMode=${transportMode}`);
  }

  initializeTempPackage(dangerousGoodId: number, packageType: EnumPackageType, transportMode: TransportMode): Observable<PackagingModel> {
    return this.http.get<PackagingModel>(`${this.languageService.getLanguage()}/EasyDgd/Package?dangerousGoodId=${dangerousGoodId}&packageType=${packageType}&transportMode=${transportMode}`);
  }

  saveSinglePackage(pkg: SinglePackagingModel): Observable<SinglePackagingModel> {
    return this.http.post<SinglePackagingModel>(`${this.languageService.getLanguage()}/EasyDgd/SinglePackage`, pkg);
  }

  duplicateSinglePackage(pkg: SinglePackagingModel): Observable<SinglePackagingModel> {
    return this.http.post<SinglePackagingModel>(`${this.languageService.getLanguage()}/EasyDgd/DuplicateSinglePackage`, pkg)
  }

  removeSinglePackage(id: string): Observable<void> {
    return this.http.delete<void>(`${this.languageService.getLanguage()}/EasyDgd/DeleteSinglePackage?id=${id}`);
  }

  saveCompositePackage(pkg: CompositePackagingModel): Observable<CompositePackagingModel> {
    return this.http.post<CompositePackagingModel>(`${this.languageService.getLanguage()}/EasyDgd/CompositePackage`, pkg);
  }

  duplicateCompositePackage(pkg: CompositePackagingModel): Observable<CompositePackagingModel> {
    return this.http.post<CompositePackagingModel>(`${this.languageService.getLanguage()}/EasyDgd/DuplicateCompositePackage`, pkg)
  }

  removeCompositePackage(id: string): Observable<void> {
    return this.http.delete<void>(`${this.languageService.getLanguage()}/EasyDgd/DeleteCompositePackage?id=${id}`);
  }

  addOverpack(data: Partial<OverPackagingModel>): Observable<OverPackagingModel> {
    return this.http.post<OverPackagingModel>(`${this.languageService.getLanguage()}/EasyDgd/Overpack`, data);
  }

  assignOverpack(packageId: string, overpackId: string): Observable<void> {
    return this.http.post<void>(`${this.languageService.getLanguage()}/EasyDgd/AssignOverpack`, { packageId, overpackId });
  }

  updateOverpack(data: OverPackagingModel): Observable<OverPackagingModel> {
    return this.http.put<OverPackagingModel>(`${this.languageService.getLanguage()}/EasyDgd/UpdateOverpack`, data);
  }

  removeOverpack(data: OverPackagingModel): Observable<void> {
    return this.http.delete<void>(`${this.languageService.getLanguage()}/EasyDgd/Overpack?id=${data.Id}`);
  }
  loadPreview(): Observable<PreviewImageViewModel> {
    return this.http.get<PreviewImageViewModel>(`${this.languageService.getLanguage()}/EasyDgd/PreviewImage`);
  }
  setPaymentType(paymentType: PaymentType): Observable<DownloadModel> {
    return this.http.post<DownloadModel>(`${this.languageService.getLanguage()}/EasyDgd/PaymentType`, paymentType);
  }
  checkout(): Observable<DownloadModel> {
    return this.http.get<DownloadModel>(`${this.languageService.getLanguage()}/EasyDgd/Checkout`).pipe(
      tap((downloadModel: DownloadModel) => {
        this.downloadModel = downloadModel;
      })
    );
  }
  share(data: ShareDocumentModel): Observable<BaseResultModel> {
    return this.http.post<BaseResultModel>(`${this.languageService.getLanguage()}/EasyDgd/Share`, data);
  }
  resetModel(): Observable<BaseResultModel> {
    return this.http.delete<BaseResultModel>(`${this.languageService.getLanguage()}/EasyDgd/Reset?transportMode=${this.getTransportMode()}`);
  }
  getProperShippingName(data: SinglePackagingModel) {
    return this.http.post<string>(`${this.languageService.getLanguage()}/EasyDgd/ProperShippingName`, data);
  }
  getPackageDetails(data: InnerPackagingModel, transportMode: TransportMode) {
    return this.http.post<InnerPackagingModel>(`${this.languageService.getLanguage()}/EasyDgd/PackageDetails?transportMode=${transportMode}`, data);
  }
  canLoadTogether(dangerousGoodId: number, transportMode: TransportMode, allDangerousGoods: GefahrgutApiDangerousGoodItem[]) {
    if (!allDangerousGoods.length || allDangerousGoods.map(dg => dg.DangerousGoodId).every(dgId => dgId === dangerousGoodId)) {
      return of({
        IsLoadTogetherAccepted: true,
        ConflictItems: []
      });
    }
    return this.http.post<CanLoadTogetherResult>(`${this.languageService.getLanguage()}/DangerousGoodDetails/CanLoadTogether`, {
      TransportMode: transportMode,
      DangerousGoodItems: [
        {
          DangerousGoodId: dangerousGoodId,
        },
        ...allDangerousGoods,
      ]
    });
  }



  private putData(model: EasyDgdDataModel): Observable<boolean> {
    return this.http.post<boolean>(`${this.languageService.getLanguage()}/EasyDgd/PutData`, model);
  }

}
